/**
 * MCP Gateway Client
 *
 * Handles communication with flows-alpha-mcp-manager for MCP server operations.
 * The MCP Manager acts as a gateway/proxy to MCP servers, handling:
 * - Server discovery (via WFM Resource APIs)
 * - Session management
 * - JSON-RPC protocol for MCP operations
 *
 * API Endpoints:
 * - GET  /mcp/list?type=MCPServerStdio     → List available MCP servers (from WFM)
 * - POST /mcp/{resource_id}                → JSON-RPC (initialize, tools/list, tools/call)
 * - DELETE /mcp/{resource_id}              → End session
 *
 * Architecture:
 * ┌─────────────────┐     ┌─────────────────┐     ┌─────────────────┐
 * │  Agent Builder  │────▶│   MCP Manager   │────▶│   WFM Resources │
 * │  (this client)  │     │  (gateway/proxy)│     │  (server defs)  │
 * └─────────────────┘     └─────────────────┘     └─────────────────┘
 *                                │
 *                                ▼
 *                         ┌─────────────────┐
 *                         │   MCP Servers   │
 *                         │ (stdio/remote)  │
 *                         └─────────────────┘
 */

import { getToken } from './auth';
import { getIdentity, getWorkflowManagerUrl } from './config/services';
import { getMcpBaseUrl } from './apiClient';
import type {
  MCPServerListItem,
  MCPTool,
  MCPResource,
  MCPSessionResponse,
} from '../types/agent-builder';

// =============================================================================
// TYPES - Matching actual API responses
// =============================================================================

/**
 * Response from GET /mcp/list
 */
interface MCPListResponse {
  status_code: number;
  data: {
    resources: Array<{
      id: string;
      name: string;
      display_name?: string;
      type: string;
      scope: 'project' | 'tenant' | 'global' | 'user';
      scope_id?: string;
      tags?: string[];
      modified_at?: string;
    }>;
    total?: number;
    total_count?: number;
  };
  message: string;
}

/**
 * JSON-RPC response wrapper
 */
interface JSONRPCResponse<T> {
  jsonrpc: '2.0';
  id: number;
  result?: T;
  error?: {
    code: number;
    message: string;
    data?: unknown;
  } | null;
}

/**
 * Initialize response result
 */
interface InitializeResult {
  _meta: null;
  protocolVersion: string;
  capabilities: {
    experimental: null;
    logging: null;
    prompts: { listChanged: null } | null;
    resources: { subscribe: null; listChanged: null } | null;
    tools: { listChanged: null } | null;
    completions: null;
    tasks: null;
  };
  serverInfo: {
    name: string;
    title: string | null;
    version: string;
    websiteUrl: string | null;
    icons: null;
  };
  instructions: string | null;
}

/**
 * Tools list response result
 */
interface ToolsListResult {
  _meta: null;
  nextCursor: string | null;
  tools: MCPTool[];
}

/**
 * Tool call response result
 */
interface ToolCallResult {
  _meta: null;
  content: Array<{
    type: 'text' | 'image' | 'resource';
    text?: string;
    annotations?: null;
    _meta?: null;
  }>;
  structuredContent?: unknown;
  isError: boolean;
}

// =============================================================================
// AUTH HELPERS
// =============================================================================

/**
 * Get authentication headers for MCP Gateway requests
 * Required headers: X-Tenant-Id, X-User-Id
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = await getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const identity = getIdentity();
    if (identity.tenantId) {
      headers['X-Tenant-Id'] = identity.tenantId;
      headers['tenant'] = identity.tenantId;
    }
    if (identity.userId) {
      headers['X-User-Id'] = identity.userId;
    }
  } catch {
    // Identity not set, continue without headers
  }

  return headers;
}

// =============================================================================
// TYPE TEMPLATE EXTRACTION
// =============================================================================

/**
 * Extract functional template name from a resource type string.
 *
 * Type format: `resources.mcp_server.<transport>.<TemplateName>`
 *  - 3 segments (e.g., "resources.mcp_server.MCPServerStdio") → no functional template (legacy/bare)
 *  - 4+ segments (e.g., "resources.mcp_server.MCPServerSSE.Tavily_Search_ETE") → template is 4th+ segment
 *
 * @param type - Full resource type string
 * @returns Functional template name or null if bare/legacy
 */
export function extractMcpTemplate(type: string): string | null {
  const segments = type.split('.');
  // Must start with "resources.mcp_server" and have at least 4 segments
  if (segments.length >= 4 && segments[0] === 'resources' && segments[1] === 'mcp_server') {
    // Template is everything after the 3rd segment (transport), joined by '.'
    // e.g., "resources.mcp_server.MCPServerSSE.Tavily_Search_ETE" → "Tavily_Search_ETE"
    return segments.slice(3).join('.');
  }
  return null;
}

// =============================================================================
// TYPE LISTING
// =============================================================================

/**
 * List available MCP server types from WFM
 *
 * GET /mcp/types
 *
 * @returns Array of MCP type strings as returned by WFM
 */
export async function listMCPTypes(): Promise<string[]> {
  const baseUrl = getWorkflowManagerUrl();
  const headers = await getAuthHeaders();

  const res = await fetch(`${baseUrl}/mcp/types`, { headers });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || error.detail || `Failed to list MCP types: ${res.statusText}`);
  }

  const data = await res.json();
  return data.data || [];
}

// =============================================================================
// SERVER LISTING
// =============================================================================

/**
 * List available MCP servers from MCP Gateway
 *
 * GET /mcp/list?type=MCPServerStdio
 *
 * Response format:
 * {
 *   "status_code": 200,
 *   "data": {
 *     "resources": [
 *       {
 *         "id": "uuid",
 *         "name": "Server Name",
 *         "type": "resources.mcp_server.MCPServerStdio",
 *         "scope": "project",
 *         "scope_id": "project-uuid",
 *         "tags": ["tag1", "tag2"]
 *       }
 *     ],
 *     "total": 2
 *   },
 *   "message": "Resources fetched successfully"
 * }
 *
 * @param type - Server type filter (e.g., 'MCPServerStdio')
 * @returns Array of available MCP servers
 */
export async function listMCPServers(type?: string): Promise<MCPServerListItem[]> {
  const baseUrl = getWorkflowManagerUrl();
  const headers = await getAuthHeaders();

  // Build URL with query params — uses WFM /mcp endpoint
  let url = `${baseUrl}/mcp`;
  if (type) {
    url += `?type=${encodeURIComponent(type)}`;
  }

  const res = await fetch(url, { headers });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || error.detail || `Failed to list MCP servers: ${res.statusText}`);
  }

  const data: MCPListResponse = await res.json();

  // Convert to MCPServerListItem format, enriching with template info
  return (data.data?.resources || []).map(r => ({
    id: r.id,
    name: r.name,
    display_name: r.display_name,
    type: r.type,
    type_template: extractMcpTemplate(r.type) || undefined,
    scope: r.scope,
    scope_id: r.scope_id,
    tags: r.tags,
  }));
}

/**
 * List MCP servers with stdio type (most common)
 */
export async function listStdioMCPServers(): Promise<MCPServerListItem[]> {
  return listMCPServers('MCPServerStdio');
}

/**
 * List only MCP servers that have a functional template mapped.
 *
 * Filters to resources with 4+ segments in type string (e.g.,
 * "resources.mcp_server.MCPServerSSE.Tavily_Search_ETE").
 * Legacy/bare resources (3 segments, no template) are excluded.
 *
 * @returns Array of MCP servers with type_template populated
 */
export async function listFunctionalMCPServers(): Promise<MCPServerListItem[]> {
  const all = await listMCPServers();
  return all.filter(s => s.type_template);
}

// =============================================================================
// SESSION MANAGEMENT
// =============================================================================

/**
 * Initialize an MCP session with a server (JSON-RPC)
 *
 * POST /mcp/{resource_id}
 * Headers: X-Tenant-Id, X-User-Id
 * Body: {
 *   "jsonrpc": "2.0",
 *   "id": 1,
 *   "method": "initialize",
 *   "params": {
 *     "protocolVersion": "2024-11-05",
 *     "capabilities": {},
 *     "clientInfo": { "name": "agent-builder", "version": "1.0.0" }
 *   }
 * }
 *
 * @param resourceId - The MCP server resource ID (UUID from /mcp/list)
 * @returns Session response with server info and capabilities
 */
export async function initializeMCPSession(resourceId: string): Promise<MCPSessionResponse> {
  const baseUrl = getMcpBaseUrl();
  const headers = await getAuthHeaders();

  const res = await fetch(`${baseUrl}/mcp/${resourceId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'initialize',
      params: {
        protocolVersion: '2024-11-05',
        capabilities: {},
        clientInfo: {
          name: 'agent-builder',
          version: '1.0.0',
        },
      },
    }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || error.detail || `Failed to initialize MCP session: ${res.statusText}`);
  }

  const data: JSONRPCResponse<InitializeResult> = await res.json();

  // Check for JSON-RPC error
  if (data.error) {
    throw new Error(data.error.message || 'Failed to initialize session');
  }

  // Session ID comes from response header (may not be present if gateway manages sessions internally)
  const sessionId = res.headers.get('Mcp-Session-Id') || '';

  return {
    sessionId,
    resourceId,
    serverInfo: data.result?.serverInfo ? {
      name: data.result.serverInfo.name,
      version: data.result.serverInfo.version,
    } : undefined,
    capabilities: data.result?.capabilities ? {
      tools: !!data.result.capabilities.tools,
      resources: !!data.result.capabilities.resources,
      prompts: !!data.result.capabilities.prompts,
    } : undefined,
  };
}

/**
 * End an MCP session
 *
 * DELETE /mcp/{resource_id}
 * Headers: X-Tenant-Id, X-User-Id, Mcp-Session-Id
 *
 * @param resourceId - The MCP server resource ID
 * @param sessionId - The session ID to terminate
 */
export async function endMCPSession(resourceId: string, sessionId: string): Promise<void> {
  const baseUrl = getMcpBaseUrl();
  const headers = await getAuthHeaders();
  headers['Mcp-Session-Id'] = sessionId;

  const res = await fetch(`${baseUrl}/mcp/${resourceId}`, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok && res.status !== 204) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || `Failed to end MCP session: ${res.statusText}`);
  }
}

// =============================================================================
// TOOL OPERATIONS
// =============================================================================

/**
 * List tools from an MCP server (JSON-RPC)
 *
 * POST /mcp/{resource_id}
 * Headers: X-Tenant-Id, X-User-Id, Mcp-Session-Id (optional, for existing session)
 * Body: { "jsonrpc": "2.0", "id": 1, "method": "tools/list" }
 *
 * @param resourceId - The MCP server resource ID
 * @param sessionId - Optional session ID from previous initialize call
 * @returns Array of available tools
 */
export async function listMCPTools(resourceId: string, sessionId?: string): Promise<MCPTool[]> {
  const baseUrl = getMcpBaseUrl();
  const headers = await getAuthHeaders();

  if (sessionId) {
    headers['Mcp-Session-Id'] = sessionId;
  }

  const res = await fetch(`${baseUrl}/mcp/${resourceId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'tools/list',
    }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || error.detail || `Failed to list MCP tools: ${res.statusText}`);
  }

  const data: JSONRPCResponse<ToolsListResult> = await res.json();

  // Check for JSON-RPC error
  if (data.error) {
    throw new Error(data.error.message || 'Failed to list tools');
  }

  return data.result?.tools || [];
}

/**
 * Call a tool on an MCP server (JSON-RPC)
 *
 * POST /mcp/{resource_id}
 * Headers: X-Tenant-Id, X-User-Id, Mcp-Session-Id (required for tool calls)
 * Body: {
 *   "jsonrpc": "2.0",
 *   "id": 3,
 *   "method": "tools/call",
 *   "params": {
 *     "name": "tool_name",
 *     "arguments": { "arg1": "value1" }
 *   }
 * }
 *
 * @param resourceId - The MCP server resource ID
 * @param toolName - Name of the tool to call
 * @param args - Arguments to pass to the tool
 * @param sessionId - Session ID (required for tool calls)
 * @returns Tool call result
 */
export async function callMCPTool(
  resourceId: string,
  toolName: string,
  args: Record<string, unknown> = {},
  sessionId?: string
): Promise<{
  content: Array<{ type: string; text?: string }>;
  structuredContent?: unknown;
  isError: boolean;
}> {
  const baseUrl = getMcpBaseUrl();
  const headers = await getAuthHeaders();

  if (sessionId) {
    headers['Mcp-Session-Id'] = sessionId;
  }

  const res = await fetch(`${baseUrl}/mcp/${resourceId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'tools/call',
      params: {
        name: toolName,
        arguments: args,
      },
    }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || error.detail || `Failed to call MCP tool: ${res.statusText}`);
  }

  const data: JSONRPCResponse<ToolCallResult> = await res.json();

  // Check for JSON-RPC error
  if (data.error) {
    throw new Error(data.error.message || 'Tool call failed');
  }

  return {
    content: data.result?.content || [],
    structuredContent: data.result?.structuredContent,
    isError: data.result?.isError || false,
  };
}

// =============================================================================
// RESOURCE OPERATIONS
// =============================================================================

/**
 * List resources from an MCP server (JSON-RPC)
 *
 * POST /mcp/{resource_id}
 * Body: { "jsonrpc": "2.0", "id": 1, "method": "resources/list" }
 *
 * @param resourceId - The MCP server resource ID
 * @param sessionId - Optional session ID
 * @returns Array of available resources
 */
export async function listMCPResources(resourceId: string, sessionId?: string): Promise<MCPResource[]> {
  const baseUrl = getMcpBaseUrl();
  const headers = await getAuthHeaders();

  if (sessionId) {
    headers['Mcp-Session-Id'] = sessionId;
  }

  const res = await fetch(`${baseUrl}/mcp/${resourceId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'resources/list',
    }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || error.detail || `Failed to list MCP resources: ${res.statusText}`);
  }

  const data = await res.json();

  // Check for JSON-RPC error
  if (data.error) {
    throw new Error(data.error.message || 'Failed to list resources');
  }

  return data.result?.resources || [];
}

/**
 * Read a resource from an MCP server (JSON-RPC)
 *
 * POST /mcp/{resource_id}
 * Body: { "jsonrpc": "2.0", "id": 1, "method": "resources/read", "params": { "uri": "..." } }
 *
 * @param resourceId - The MCP server resource ID
 * @param uri - Resource URI to read
 * @param sessionId - Optional session ID
 * @returns Resource content
 */
export async function readMCPResource(
  resourceId: string,
  uri: string,
  sessionId?: string
): Promise<unknown> {
  const baseUrl = getMcpBaseUrl();
  const headers = await getAuthHeaders();

  if (sessionId) {
    headers['Mcp-Session-Id'] = sessionId;
  }

  const res = await fetch(`${baseUrl}/mcp/${resourceId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method: 'resources/read',
      params: { uri },
    }),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || error.detail || `Failed to read MCP resource: ${res.statusText}`);
  }

  const data = await res.json();

  // Check for JSON-RPC error
  if (data.error) {
    throw new Error(data.error.message || 'Failed to read resource');
  }

  return data.result?.contents || data.result;
}

// =============================================================================
// LLM RESOURCE LISTING
// =============================================================================

/**
 * LLM Resource from MCP Gateway
 */
export interface GatewayLLMResource {
  id: string;
  name: string;
  display_name?: string;
  type: string;
  provider?: string;
}

/**
 * List available LLM resources from MCP Gateway
 *
 * GET /mcp/list (without type filter) returns all resources including LLMs.
 * We filter for types starting with "resources.llm.".
 *
 * @returns Array of LLM resources compatible with wfmClient.LLMResource
 */
export async function listLLMResources(): Promise<GatewayLLMResource[]> {
  const baseUrl = getWorkflowManagerUrl();
  const headers = await getAuthHeaders();

  const res = await fetch(`${baseUrl}/mcp`, { headers });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(error.message || error.detail || `Failed to list resources: ${res.statusText}`);
  }

  const data: MCPListResponse = await res.json();

  // Filter for LLM resources (type starts with "resources.llm.")
  return (data.data?.resources || [])
    .filter(r => r.type.startsWith('resources.llm.'))
    .map(r => ({
      id: r.id,
      name: r.name,
      display_name: r.display_name,
      type: r.type,
      // Extract provider from type: "resources.llm.LiteLLMV2" -> "LiteLLMV2"
      provider: r.type.replace('resources.llm.', ''),
    }));
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get server details including tools and resources
 * Convenience function that initializes session and fetches all info
 *
 * @param resourceId - The MCP server resource ID
 * @returns Server info with tools and resources
 */
export async function getMCPServerDetails(resourceId: string): Promise<{
  server: MCPSessionResponse;
  tools: MCPTool[];
  resources: MCPResource[];
}> {
  // Initialize session first
  const server = await initializeMCPSession(resourceId);

  // Fetch tools and resources in parallel
  const [tools, resources] = await Promise.all([
    listMCPTools(resourceId, server.sessionId).catch(() => []),
    listMCPResources(resourceId, server.sessionId).catch(() => []),
  ]);

  return { server, tools, resources };
}

/**
 * Check if an MCP server is available
 *
 * @param resourceId - The MCP server resource ID
 * @returns True if server responds to initialize
 */
export async function isMCPServerAvailable(resourceId: string): Promise<boolean> {
  try {
    await initializeMCPSession(resourceId);
    return true;
  } catch {
    return false;
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export const mcpGatewayClient = {
  // Type listing
  listMCPTypes,

  // Server listing
  listMCPServers,
  listStdioMCPServers,
  listFunctionalMCPServers,

  // Template extraction
  extractMcpTemplate,

  // LLM listing
  listLLMResources,

  // Session management
  initializeMCPSession,
  endMCPSession,

  // Tool operations
  listMCPTools,
  callMCPTool,

  // Resource operations
  listMCPResources,
  readMCPResource,

  // Utilities
  getMCPServerDetails,
  isMCPServerAvailable,
};
